/**
 * Display-currency formatting.
 *
 * ShipDesi stores and charges every amount in INR. These helpers only change how
 * an amount is SHOWN, using the viewer's display currency and the daily rates
 * from GET /api/fx/rates (1 INR = rate × currency).
 *
 * The rule, everywhere:
 *   • Viewer currency is INR → plain "₹1,030".
 *   • Normal screens (lists, dashboards, details) → the viewer's currency is
 *     primary ("$12.40") and the INR figure is secondary (tooltip / small text).
 *   • Pay screens (checkout, wallet top-up, paying a quote/invoice) → INR is
 *     primary because that is what gets charged: "₹1,030 ≈ $12.40".
 *   • Staff (admin/warehouse) see INR unless they pick another currency.
 *
 * State lives in a tiny module store so plain helpers (used inside tables and
 * template strings) and the React hook (`useMoney`) read the same values.
 * <CurrencyProvider> fills it once the user and rates are known.
 */

export const BASE = 'INR';

// Fallback until the API answers (mirrors backend core/fx.py PLACEHOLDER_RATES).
export const PLACEHOLDER_RATES = Object.freeze({
  INR: 1,
  USD: 0.0119,
  GBP: 0.0089,
  EUR: 0.0102,
  CAD: 0.0164,
  AUD: 0.0182,
  AED: 0.0437,
  SGD: 0.0153,
});

export const CURRENCIES = Object.freeze([
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
]);

// ── store ────────────────────────────────────────────────────────────────────
let state = { currency: BASE, rates: { ...PLACEHOLDER_RATES }, fetchedAt: null, version: 0 };
const listeners = new Set();

export function getMoneyState() {
  return state;
}

export function setMoneyState(patch) {
  const next = { ...state, ...patch };
  next.currency = normalizeCurrency(next.currency) || BASE;
  next.rates = { ...PLACEHOLDER_RATES, ...(next.rates || {}) };
  next.version = state.version + 1;
  state = next;
  listeners.forEach((fn) => fn());
}

export function subscribeMoney(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function resetMoneyState() {
  state = { currency: BASE, rates: { ...PLACEHOLDER_RATES }, fetchedAt: null, version: state.version + 1 };
  listeners.forEach((fn) => fn());
}

// ── pure helpers ─────────────────────────────────────────────────────────────
export function normalizeCurrency(code) {
  if (typeof code !== 'string') return null;
  const c = code.trim().toUpperCase();
  return CURRENCIES.some((x) => x.code === c) ? c : null;
}

export function toNumber(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function rateFor(currency, rates) {
  const r = Number((rates || {})[currency]);
  return Number.isFinite(r) && r > 0 ? r : null;
}

/** INR → currency, or null when the amount or rate is unusable. */
export function convertFromInr(inr, currency, rates = state.rates) {
  const n = toNumber(inr);
  if (n === null) return null;
  if (currency === BASE) return n;
  const r = rateFor(currency, rates);
  return r === null ? null : n * r;
}

/** currency → INR, or null when the amount or rate is unusable. */
export function convertToInr(amount, currency, rates = state.rates) {
  const n = toNumber(amount);
  if (n === null) return null;
  if (!currency || currency === BASE) return n;
  const r = rateFor(currency, rates);
  return r === null ? null : n / r;
}

/**
 * INR price of a carrier rate / packing add-on. The API's legacy `price_usd`
 * field actually holds INR; newer responses also send `price_inr`.
 */
export function priceInr(obj) {
  if (!obj) return 0;
  return toNumber(obj.price_inr) ?? toNumber(obj.price_usd) ?? 0;
}

/** "₹1,030" / "$12.40". Whole numbers drop the decimals unless `decimals` is set. */
export function formatCurrency(amount, currency = BASE, { decimals } = {}) {
  const n = toNumber(amount);
  if (n === null) return '—';
  const digits = decimals ?? (Number.isInteger(n) ? 0 : 2);
  try {
    return new Intl.NumberFormat(currency === BASE ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(digits)}`;
  }
}

export const formatInr = (inr, opts) => formatCurrency(inr, BASE, opts);

/**
 * All the pieces needed to show an INR amount.
 * opts: { currency, rates, payment, decimals }
 * Returns { primary, secondary, title, text, converted }.
 */
export function moneyParts(inr, opts = {}) {
  const currency = normalizeCurrency(opts.currency) || state.currency;
  const rates = opts.rates || state.rates;
  const inrText = formatInr(inr, { decimals: opts.decimals });
  const local = currency === BASE ? null : convertFromInr(inr, currency, rates);
  if (local === null) {
    return { primary: inrText, secondary: null, title: undefined, text: inrText, converted: false };
  }
  const localText = formatCurrency(local, currency, { decimals: 2 });
  if (opts.payment) {
    return {
      primary: inrText,
      secondary: `≈ ${localText}`,
      title: `Charged in INR. ${localText} is an estimate at today's rate.`,
      text: `${inrText} ≈ ${localText}`,
      converted: true,
    };
  }
  return {
    primary: localText,
    secondary: inrText,
    title: `${inrText} (amounts are charged in INR)`,
    text: localText,
    converted: true,
  };
}

/** Display text: viewer currency primary. */
export const fmtMoneyText = (inr, opts) => moneyParts(inr, opts).text;

/** Pay-screen text: INR primary with the local estimate. */
export const fmtPayText = (inr, opts) => moneyParts(inr, { ...opts, payment: true }).text;

/**
 * Integer minor units in `fromCurrency` (3PL money) → display text.
 * Converts through INR rates; shows the stored currency as-is when no rate exists.
 */
export function fmtMinorText(minor, fromCurrency = BASE, opts = {}) {
  const n = toNumber(minor);
  if (n === null) return '—';
  const from = (fromCurrency || BASE).toUpperCase();
  const major = n / 100;
  const inr = convertToInr(major, from, opts.rates || state.rates);
  if (inr === null) return formatCurrency(major, from, { decimals: 2 });
  return moneyParts(inr, { decimals: 2, ...opts }).text;
}
